import type {
  QboCandidate,
  QboExportPreview,
  QboExportRequest,
  QboLineStatus,
  QboMappingsRequest,
  QboSettingsPatch,
  QboStatus,
  QboSyncLog,
} from '@siteday/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Download, RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import {
  equipmentApi,
  errorMessage,
  IS_MOCK,
  isApiError,
  jobsApi,
  qk,
  quickbooksApi,
  usersApi,
} from '@/api';
import { EmptyState, ErrorState, LoadingState, PageHeader } from '@/components/PageHeader';
import { Pill } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Input, Select } from '@/components/ui/input';
import { Card, CardBody, CardHeader, Table } from '@/components/ui/table';
import { Tabs } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/toast';
import { CheckboxChip } from '@/components/ui/toggle';
import { useCompanyBasics } from '@/features/auth/session';
import { useJobsList } from '@/features/jobs/useJobs';
import { cn } from '@/lib/cn';
import { fmtDateMedium, fmtWhen, todayInTz, weekStartFor } from '@/lib/dates';
import { hrs } from '@/lib/format';
import { useUrlState } from '@/lib/url-state';

/**
 * W10 QuickBooks (doc 08; doc 10; tasks Q-3, Q-5). Talks to the doc 05
 * `/quickbooks/*` endpoints through the shared contract; import and export
 * runs are queued by the API and this screen polls their sync log.
 */

/** `job` is the cost-code tab's job; the export scope keeps its own `xjob` so the two never leak into each other. */
const DEFAULTS = { tab: 'jobs', job: '', xjob: '', week: '', scope: 'all' };
const TABS = [
  { key: 'jobs', label: 'Jobs ↔ Customers' },
  { key: 'codes', label: 'Cost codes ↔ Items' },
  { key: 'people', label: 'People ↔ Employees' },
  { key: 'equipment', label: 'Equipment ↔ Vendors' },
  { key: 'export', label: 'Export' },
  { key: 'log', label: 'Sync log' },
];
const POLL_MS = 700;
const REASON_TAB: Record<QboLineStatus, string> = {
  mapped: 'export',
  UNMAPPED_EMPLOYEE: 'people',
  UNMAPPED_VENDOR: 'equipment',
  UNMAPPED_CUSTOMER: 'jobs',
  UNMAPPED_ITEM: 'codes',
};
const REASON_LABEL: Record<string, string> = {
  UNMAPPED_EMPLOYEE: 'person not mapped to an employee',
  UNMAPPED_VENDOR: 'machine not mapped to a vendor',
  UNMAPPED_CUSTOMER: 'job not mapped to a customer',
  UNMAPPED_ITEM: 'cost code not mapped to a service item',
  INTUIT_FAULT: 'QuickBooks rejected the line',
  HTTP: 'QuickBooks did not answer',
  TOKEN: 'connection lost',
  RUN_FAILED: 'run stopped',
};

function useQboStatus() {
  return useQuery({ queryKey: qk.quickbooks.status(), queryFn: quickbooksApi.getStatus });
}

/** Poll one sync log until it leaves `running` (in-process queue on a laptop, BullMQ elsewhere). */
async function waitForLog(id: string, signal?: { cancelled: boolean }): Promise<QboSyncLog> {
  for (;;) {
    const log = await quickbooksApi.getLog(id);
    if (log.status !== 'running' || signal?.cancelled) return log;
    await new Promise((r) => setTimeout(r, POLL_MS));
  }
}

export function QuickBooksPage() {
  const status = useQboStatus();
  const qc = useQueryClient();
  const [state, setState] = useUrlState(DEFAULTS);
  const toast = useToast();
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const { tz } = useCompanyBasics();
  const [params, setParams] = useSearchParams();
  const invalidate = () => void qc.invalidateQueries({ queryKey: qk.quickbooks.all() });

  // Back from Intuit: the API callback lands on /quickbooks?connected=1 or ?error=…
  const announced = useRef(false);
  useEffect(() => {
    if (announced.current) return;
    const connected = params.get('connected');
    const error = params.get('error');
    if (!connected && !error) return;
    announced.current = true;
    if (connected) toast.success('Connected to QuickBooks Online');
    if (error) toast.error(`QuickBooks connect failed: ${error.replace(/_/g, ' ')}`);
    const next = new URLSearchParams(params);
    next.delete('connected');
    next.delete('error');
    setParams(next, { replace: true });
    invalidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = useMutation({
    mutationFn: async () => {
      if (IS_MOCK) return quickbooksApi.connectViaFetch();
      window.location.href = quickbooksApi.connectUrl();
      return null;
    },
    onSuccess: () => {
      invalidate();
      if (IS_MOCK) toast.success('Connected to the QuickBooks sandbox (mock)');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const disconnect = useMutation({
    mutationFn: quickbooksApi.disconnect,
    onSuccess: () => {
      invalidate();
      setConfirmDisconnect(false);
      toast.success('Disconnected');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const runImport = useMutation({
    mutationFn: async () => {
      const { syncLogId } = await quickbooksApi.runImport();
      invalidate();
      return waitForLog(syncLogId);
    },
    onSuccess: (log) => {
      invalidate();
      if (log.status === 'ok') toast.success(`Import finished — ${log.summary ?? 'candidates refreshed'}`);
      else toast.error(`Import ${log.status}: ${log.summary ?? 'see the sync log'}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (status.isPending) return <LoadingState />;
  if (status.isError)
    return <ErrorState error={status.error} onRetry={() => void status.refetch()} />;
  const s = status.data;

  if (!s.connected) {
    return (
      <>
        <PageHeader title="QuickBooks Online" />
        <Card className="max-w-2xl">
          <CardBody className="flex flex-col gap-3 p-6">
            <Pill tone={s.disconnectedReason ? 'warn' : 'neutral'}>
              {s.disconnectedReason ? 'Disconnected' : 'Not connected'}
            </Pill>
            <h2 className="font-cond text-lg font-extrabold">
              {s.disconnectedReason
                ? 'QuickBooks needs to be reconnected'
                : 'Send approved hours to QuickBooks Online'}
            </h2>
            {s.disconnectedReason ? (
              <p className="m-0 text-ink2" data-testid="qbo-disconnected-reason">
                QuickBooks Online dropped the connection ({s.disconnectedReason.replace(/_/g, ' ')}
                ). Exports and imports are paused until an owner or admin reconnects. Mappings and
                everything already exported are kept.
              </p>
            ) : (
              <p className="m-0 text-ink2">
                Connecting lets SiteDay import your customers, service items, classes, employees
                and vendors so jobs, cost codes, people and equipment can be mapped. Approved days
                then export as time activities (or a CSV in the QuickBooks import layout). Nothing
                is sent until you run an export.
              </p>
            )}
            <ul className="m-0 list-disc pl-5 text-[13px] text-ink2">
              <li>QuickBooks Online only. QuickBooks Desktop uses the CSV export mode.</li>
              <li>You can disconnect any time; mappings are kept.</li>
              <li>Quantities always go to a CSV — QuickBooks has no object for them.</li>
            </ul>
            {!s.configured ? (
              <p className="m-0 text-[13px] font-semibold text-warn">
                This server has no QuickBooks app keys yet (QBO_CLIENT_ID / QBO_CLIENT_SECRET).
                Connect is disabled until they are set.
              </p>
            ) : null}
            <div>
              <Button
                variant="primary"
                onClick={() => connect.mutate()}
                loading={connect.isPending}
                disabled={!s.configured}
                data-testid="qbo-connect"
              >
                {s.disconnectedReason ? 'Reconnect to QuickBooks' : 'Connect to QuickBooks'}
              </Button>
            </div>
          </CardBody>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="QuickBooks Online"
        actions={
          <>
            <Button onClick={() => runImport.mutate()} loading={runImport.isPending}>
              <RefreshCw /> {runImport.isPending ? 'Importing…' : 'Import now'}
            </Button>
            <Button variant="danger" onClick={() => setConfirmDisconnect(true)}>
              Disconnect
            </Button>
          </>
        }
      />
      {s.reconnectSoon ? (
        <div
          role="alert"
          className="mb-3 rounded-md border border-warn bg-surface2 px-4 py-2 text-[13px] text-ink"
        >
          The QuickBooks connection expires{' '}
          {s.refreshExpiresAt ? fmtDateMedium(s.refreshExpiresAt.slice(0, 10)) : 'soon'}. Reconnect
          before then to keep exports running.
        </div>
      ) : null}
      <Card>
        <CardBody className="flex flex-wrap items-center gap-5">
          <Pill tone="good">● Connected</Pill>
          <span className="text-lg font-extrabold">
            {s.companyName ?? 'QuickBooks company'}{' '}
            <span className="text-[13px] font-medium text-ink3">{s.environment}</span>
          </span>
          <KV label="Realm" value={s.realmId ?? '—'} />
          <KV label="Export mode" value={exportModeLabel(s)} />
          <KV
            label="Last import"
            value={
              s.lastImport
                ? `${fmtWhen(s.lastImport, tz)}${s.lastImportSummary ? ` · ${s.lastImportSummary}` : ''}`
                : 'never'
            }
          />
          <KV
            label="Last export"
            value={
              s.lastExport
                ? `${fmtWhen(s.lastExport, tz)}${s.lastExportSummary ? ` · ${s.lastExportSummary}` : ''}`
                : 'never'
            }
          />
        </CardBody>
      </Card>
      <Tabs className="mt-4" items={TABS} value={state.tab} onChange={(tab) => setState({ tab })} />
      {state.tab === 'jobs' ? <JobsMapping /> : null}
      {state.tab === 'codes' ? (
        <CostCodesMapping jobId={state.job} onJob={(job) => setState({ job })} />
      ) : null}
      {state.tab === 'people' ? <PeopleMapping /> : null}
      {state.tab === 'equipment' ? <EquipmentMapping status={s} /> : null}
      {state.tab === 'export' ? (
        <ExportPanel
          status={s}
          scope={state.scope}
          job={state.xjob}
          week={state.week}
          onChange={(p) => setState({ ...(p.scope !== undefined ? { scope: p.scope } : {}), ...(p.job !== undefined ? { xjob: p.job } : {}), ...(p.week !== undefined ? { week: p.week } : {}) })}
          onFix={(tab) => setState({ tab })}
        />
      ) : null}
      {state.tab === 'log' ? <SyncLog /> : null}
      <ConfirmDialog
        open={confirmDisconnect}
        onClose={() => setConfirmDisconnect(false)}
        onConfirm={() => disconnect.mutate()}
        title="Disconnect QuickBooks?"
        confirmLabel="Disconnect"
        danger
        loading={disconnect.isPending}
      >
        Exports stop until you reconnect. Existing mappings and exported line ids are kept.
      </ConfirmDialog>
    </>
  );
}

function exportModeLabel(s: QboStatus): string {
  if (s.exportMode === 'csv') return 'CSV (QuickBooks import layout)';
  return s.exportEquipment
    ? 'Time activities (labor + equipment as vendor time)'
    : 'Time activities (labor) · equipment as CSV';
}

function KV({ label, value }: { label: string; value: string }) {
  return (
    <span className="text-xs text-ink3">
      {label}
      <b className="block text-[13px] font-semibold text-ink num">{value}</b>
    </span>
  );
}

function useCandidates() {
  return useQuery({
    queryKey: qk.quickbooks.candidates(),
    queryFn: quickbooksApi.getCandidates,
    staleTime: 5 * 60_000,
  });
}

function useSaveMappings(onDone?: () => void) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (m: QboMappingsRequest) => quickbooksApi.putMappings(m),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: qk.jobs.all() });
      void qc.invalidateQueries({ queryKey: qk.users.all() });
      void qc.invalidateQueries({ queryKey: qk.equipment.all() });
      void qc.invalidateQueries({ queryKey: qk.costCodes.all() });
      void qc.invalidateQueries({ queryKey: ['quickbooks', 'preview'] });
      const n =
        res.updated.jobs + res.updated.costCodes + res.updated.users + res.updated.equipment;
      toast.success(`Mappings saved (${n} updated)`);
      onDone?.();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
}

const candidateLabel = (c: QboCandidate) => c.fullyQualifiedName || c.name;

function MappingSelect({
  value,
  options,
  onChange,
  label,
  suggestion,
}: {
  value: string;
  options: QboCandidate[];
  onChange: (v: string) => void;
  label: string;
  suggestion?: string | undefined;
}) {
  return (
    <Select
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn('h-7 w-auto max-w-72 text-xs', !value && suggestion && 'text-ink3')}
    >
      <option value="">{suggestion ? `Suggest: ${suggestion}` : 'Not mapped'}</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {candidateLabel(o)}
        </option>
      ))}
    </Select>
  );
}

function ImportHint({ importedAt }: { importedAt: string | null | undefined }) {
  const { tz } = useCompanyBasics();
  if (importedAt === undefined) return null;
  return (
    <span className="text-xs text-ink3">
      {importedAt ? `Candidates from ${fmtWhen(importedAt, tz)}` : 'No import yet — click Import now'}
    </span>
  );
}

function JobsMapping() {
  const jobs = useJobsList({});
  const cands = useCandidates();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const save = useSaveMappings(() => setDraft({}));
  const rows = (jobs.data?.items ?? []).filter((j) => j.status !== 'complete' || j.qboCustomerId);
  if (cands.isError) return <ErrorState error={cands.error} onRetry={() => void cands.refetch()} />;
  const suggest = (name: string) =>
    cands.data?.customers.find(
      (c) =>
        candidateLabel(c)
          .toLowerCase()
          .includes(name.split(' ')[0]!.toLowerCase()) &&
        !rows.some((j) => j.qboCustomerId === c.id),
    );
  const dirty = Object.keys(draft).length > 0;
  return (
    <Card>
      <CardHeader
        title="Jobs ↔ Customers / sub-customers / projects"
        right={
          <Button
            variant="primary"
            size="sm"
            disabled={!dirty}
            loading={save.isPending}
            onClick={() =>
              save.mutate({
                jobs: Object.entries(draft).map(([jobId, qboCustomerId]) => ({
                  jobId,
                  qboCustomerId: qboCustomerId || null,
                })),
              })
            }
          >
            Save mappings
          </Button>
        }
      >
        <ImportHint importedAt={cands.data?.importedAt} />
      </CardHeader>
      <Table>
        <thead>
          <tr>
            <th>SiteDay job</th>
            <th>QuickBooks customer</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((j) => {
            const value = draft[j.id] ?? j.qboCustomerId ?? '';
            const sug = !value && j.createdByForeman ? suggest(j.name) : undefined;
            const chosen = cands.data?.customers.find((c) => c.id === value);
            return (
              <tr key={j.id} className={cn(j.createdByForeman && !j.qboCustomerId && 'hi')}>
                <td>
                  {j.name} {j.createdByForeman ? <Pill tone="orange">field</Pill> : null}
                </td>
                <td>
                  <MappingSelect
                    label={`Customer for ${j.name}`}
                    value={value}
                    options={cands.data?.customers ?? []}
                    onChange={(v) => setDraft((d) => ({ ...d, [j.id]: v }))}
                    suggestion={sug ? candidateLabel(sug) : undefined}
                  />
                </td>
                <td>
                  {sug ? (
                    <Button size="sm" onClick={() => setDraft((d) => ({ ...d, [j.id]: sug.id }))}>
                      Use suggestion
                    </Button>
                  ) : value ? (
                    <>
                      <Pill tone="good">mapped</Pill>
                      {chosen?.isProject ? (
                        <Pill tone="info" className="ml-1">
                          project
                        </Pill>
                      ) : null}
                    </>
                  ) : (
                    <Pill tone="warn">unmapped</Pill>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Card>
  );
}

function CostCodesMapping({ jobId, onJob }: { jobId: string; onJob: (id: string) => void }) {
  const jobs = useJobsList({});
  const cands = useCandidates();
  const effectiveJob = jobId || jobs.data?.items[0]?.id || '';
  const codes = useQuery({
    queryKey: qk.costCodes.list(effectiveJob),
    queryFn: () => jobsApi.listCostCodes(effectiveJob),
    enabled: !!effectiveJob,
  });
  const [draft, setDraft] = useState<
    Record<string, { qboItemId?: string | null; qboClassId?: string | null }>
  >({});
  const [applyAll, setApplyAll] = useState(true);
  const save = useSaveMappings(() => setDraft({}));
  useEffect(() => {
    if (!jobId && jobs.data?.items[0]) onJob(jobs.data.items[0].id);
  }, [jobId, jobs.data, onJob]);

  const onSave = () =>
    save.mutate({
      costCodes: Object.entries(draft).map(([id, m]) => ({ id, ...m })),
      // doc 10 / W10: the API copies each mapping to every job's code with the same `code`
      applyToSameCode: applyAll,
    });
  /** doc 10: the import suggests an Item whose name starts with the code; never applied automatically. */
  const suggestion = (code: string) => cands.data?.suggestions.find((s) => s.code === code);
  const classTracking = cands.data?.classTracking ?? true;
  return (
    <Card>
      <CardHeader title="Cost codes ↔ Items or Classes">
        <Select
          aria-label="Job"
          value={effectiveJob}
          onChange={(e) => onJob(e.target.value)}
          className="h-7 w-auto text-xs"
        >
          {jobs.data?.items.map((j) => (
            <option key={j.id} value={j.id}>
              {j.name}
            </option>
          ))}
        </Select>
        <ImportHint importedAt={cands.data?.importedAt} />
        <div className="ml-auto flex items-center gap-2">
          <CheckboxChip
            label="Apply to all jobs with the same code"
            checked={applyAll}
            onChange={setApplyAll}
            className="h-7 text-xs"
          />
          <Button
            variant="primary"
            size="sm"
            disabled={Object.keys(draft).length === 0}
            loading={save.isPending}
            onClick={onSave}
          >
            Save mappings
          </Button>
        </div>
      </CardHeader>
      <Table>
        <thead>
          <tr>
            <th>Code</th>
            <th>Description</th>
            <th>Service item</th>
            <th>Class{classTracking ? '' : ' (off in QuickBooks)'}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {codes.data?.items
            .filter((c) => !c.archived)
            .map((c) => {
              const d = draft[c.id] ?? {};
              const item = d.qboItemId !== undefined ? (d.qboItemId ?? '') : (c.qboItemId ?? '');
              const cls = d.qboClassId !== undefined ? (d.qboClassId ?? '') : (c.qboClassId ?? '');
              const sug = !item ? suggestion(c.code) : undefined;
              return (
                <tr key={c.id}>
                  <td>
                    <code>{c.code}</code>
                  </td>
                  <td>{c.description}</td>
                  <td>
                    <MappingSelect
                      label={`Item for ${c.code}`}
                      value={item}
                      options={cands.data?.items ?? []}
                      onChange={(v) =>
                        setDraft((x) => ({ ...x, [c.id]: { ...x[c.id], qboItemId: v || null } }))
                      }
                      suggestion={sug?.itemName}
                    />
                  </td>
                  <td>
                    <MappingSelect
                      label={`Class for ${c.code}`}
                      value={cls}
                      options={cands.data?.classes ?? []}
                      onChange={(v) =>
                        setDraft((x) => ({ ...x, [c.id]: { ...x[c.id], qboClassId: v || null } }))
                      }
                    />
                  </td>
                  <td>
                    {sug ? (
                      <Button
                        size="sm"
                        onClick={() =>
                          setDraft((x) => ({
                            ...x,
                            [c.id]: { ...x[c.id], qboItemId: sug.qboItemId },
                          }))
                        }
                      >
                        Use suggestion
                      </Button>
                    ) : item ? (
                      <Pill tone="good">mapped</Pill>
                    ) : (
                      <Pill tone="warn">unmapped</Pill>
                    )}
                  </td>
                </tr>
              );
            })}
        </tbody>
      </Table>
    </Card>
  );
}

function PeopleMapping() {
  const users = useQuery({
    queryKey: qk.users.list({ active: 'true' }),
    queryFn: () => usersApi.listUsers({ active: 'true' }),
  });
  const cands = useCandidates();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const save = useSaveMappings(() => setDraft({}));
  const rows = (users.data?.items ?? []).filter((u) => u.role === 'foreman' || u.role === 'worker');
  return (
    <Card>
      <CardHeader
        title="People ↔ Employees"
        right={
          <Button
            variant="primary"
            size="sm"
            disabled={Object.keys(draft).length === 0}
            loading={save.isPending}
            onClick={() =>
              save.mutate({
                users: Object.entries(draft).map(([userId, qboEmployeeId]) => ({
                  userId,
                  qboEmployeeId: qboEmployeeId || null,
                })),
              })
            }
          >
            Save mappings
          </Button>
        }
      >
        <ImportHint importedAt={cands.data?.importedAt} />
      </CardHeader>
      <Table>
        <thead>
          <tr>
            <th>Person</th>
            <th>QuickBooks employee</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((u) => {
            const value = draft[u.id] ?? u.qboEmployeeId ?? '';
            const last = u.name.split(' ').pop()!.toLowerCase();
            const sug = !value
              ? cands.data?.employees.find((e) => candidateLabel(e).toLowerCase().includes(last))
              : undefined;
            return (
              <tr key={u.id}>
                <td>{u.name}</td>
                <td>
                  <MappingSelect
                    label={`Employee for ${u.name}`}
                    value={value}
                    options={cands.data?.employees ?? []}
                    onChange={(v) => setDraft((d) => ({ ...d, [u.id]: v }))}
                    suggestion={sug ? candidateLabel(sug) : undefined}
                  />
                </td>
                <td>
                  {sug ? (
                    <Button size="sm" onClick={() => setDraft((d) => ({ ...d, [u.id]: sug.id }))}>
                      Use suggestion
                    </Button>
                  ) : value ? (
                    <Pill tone="good">mapped</Pill>
                  ) : (
                    <Pill tone="warn">unmapped</Pill>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Card>
  );
}

function EquipmentMapping({ status }: { status: QboStatus }) {
  const eq = useQuery({
    queryKey: qk.equipment.list({ active: 'true' }),
    queryFn: () => equipmentApi.listEquipment({ active: 'true' }),
  });
  const cands = useCandidates();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const save = useSaveMappings(() => setDraft({}));
  return (
    <Card>
      <CardHeader
        title="Equipment ↔ Vendors"
        right={
          <Button
            variant="primary"
            size="sm"
            disabled={Object.keys(draft).length === 0}
            loading={save.isPending}
            onClick={() =>
              save.mutate({
                equipment: Object.entries(draft).map(([id, qboVendorId]) => ({
                  id,
                  qboVendorId: qboVendorId || null,
                })),
              })
            }
          >
            Save mappings
          </Button>
        }
      >
        <span className="text-xs text-ink3">
          {status.exportEquipment
            ? 'Machine hours export as vendor time activities (service item from the day’s cost code).'
            : 'Machine hours go to the quantities/equipment CSV. Turn on "Export equipment as vendor time" in the Export tab to send them as vendor time activities.'}
        </span>
      </CardHeader>
      <Table>
        <thead>
          <tr>
            <th>Machine</th>
            <th>QuickBooks vendor</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {eq.data?.items.map((e) => {
            const value = draft[e.id] ?? e.qboVendorId ?? '';
            return (
              <tr key={e.id}>
                <td>
                  {e.name} <span className="text-ink3">{e.unitNumber}</span>
                </td>
                <td>
                  <MappingSelect
                    label={`Vendor for ${e.name}`}
                    value={value}
                    options={cands.data?.vendors ?? []}
                    onChange={(v) => setDraft((d) => ({ ...d, [e.id]: v }))}
                  />
                </td>
                <td>
                  {value ? <Pill tone="good">mapped</Pill> : <Pill tone="warn">unmapped</Pill>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Card>
  );
}

function ExportSettings({ status }: { status: QboStatus }) {
  const qc = useQueryClient();
  const toast = useToast();
  const patch = useMutation({
    mutationFn: (p: QboSettingsPatch) => quickbooksApi.patchSettings(p),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.quickbooks.all() });
      toast.success('Export settings saved');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        aria-label="Export mode"
        value={status.exportMode}
        onChange={(e) => patch.mutate({ exportMode: e.target.value as QboStatus['exportMode'] })}
        className="h-7 w-auto text-xs"
      >
        <option value="time_activity">Mode · Time activities</option>
        <option value="csv">Mode · CSV files</option>
      </Select>
      <Select
        aria-label="Labor cost code rule"
        value={status.laborCostCodeRule}
        onChange={(e) =>
          patch.mutate({ laborCostCodeRule: e.target.value as QboStatus['laborCostCodeRule'] })
        }
        className="h-7 w-auto text-xs"
      >
        <option value="single">Labor → single cost code</option>
        <option value="proportional">Labor → split by quantity share</option>
      </Select>
      <CheckboxChip
        label="Export equipment as vendor time"
        checked={status.exportEquipment}
        onChange={(v) => patch.mutate({ exportEquipment: v })}
        className="h-7 text-xs"
      />
    </div>
  );
}

function ExportPanel({
  status,
  scope,
  job,
  week,
  onChange,
  onFix,
}: {
  status: QboStatus;
  scope: string;
  job: string;
  week: string;
  onChange: (p: { scope?: string; job?: string; week?: string }) => void;
  onFix: (tab: string) => void;
}) {
  const { tz } = useCompanyBasics();
  const jobs = useJobsList({});
  const qc = useQueryClient();
  const toast = useToast();
  const effectiveScope = job || week ? 'week' : scope;
  const params: QboExportRequest =
    effectiveScope === 'week'
      ? {
          ...(job ? { jobId: job } : {}),
          weekStart: week || weekStartFor(todayInTz(tz), 'mon'),
        }
      : {};
  const preview = useQuery({
    queryKey: qk.quickbooks.preview({ jobId: params.jobId, weekStart: params.weekStart }),
    queryFn: () => quickbooksApi.getExportPreview(params),
  });
  const run = useMutation({
    mutationFn: async () => {
      const { syncLogId } = await quickbooksApi.runExport(params);
      void qc.invalidateQueries({ queryKey: qk.quickbooks.logs() });
      return waitForLog(syncLogId);
    },
    onSuccess: (log) => {
      void qc.invalidateQueries({ queryKey: qk.quickbooks.all() });
      void qc.invalidateQueries({ queryKey: qk.entries.all() });
      if (log.status === 'ok') toast.success(`Export finished — ${log.summary ?? 'see the sync log'}`);
      else toast.error(`Export ${log.status} — ${log.summary ?? 'see the sync log'}`);
    },
    onError: (e) =>
      toast.error(
        isApiError(e) && e.code === 'QBO_NOT_CONNECTED'
          ? 'Connect QuickBooks first.'
          : errorMessage(e),
      ),
  });
  const isCsv = status.exportMode === 'csv';
  return (
    <Card>
      <CardHeader title="Export">
        <Select
          aria-label="Scope"
          value={effectiveScope}
          onChange={(e) =>
            onChange(
              e.target.value === 'all'
                ? { scope: 'all', job: '', week: '' }
                : { scope: 'week', week: week || weekStartFor(todayInTz(tz), 'mon') },
            )
          }
          className="h-7 w-auto text-xs"
        >
          <option value="all">Scope · Everything approved and unexported</option>
          <option value="week">Scope · One job and week</option>
        </Select>
        {effectiveScope === 'week' ? (
          <>
            <Select
              aria-label="Job"
              value={job}
              onChange={(e) => onChange({ job: e.target.value })}
              className="h-7 w-auto text-xs"
            >
              <option value="">All jobs</option>
              {jobs.data?.items.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.name}
                </option>
              ))}
            </Select>
            <Input
              type="date"
              aria-label="Week"
              value={params.weekStart ?? ''}
              onChange={(e) =>
                e.target.value && onChange({ week: weekStartFor(e.target.value, 'mon') })
              }
              className="h-7 w-auto text-xs"
            />
          </>
        ) : null}
        <div className="ml-auto">
          <ExportSettings status={status} />
        </div>
      </CardHeader>
      {preview.isPending ? (
        <LoadingState />
      ) : preview.isError ? (
        <CardBody>
          <ErrorState error={preview.error} onRetry={() => void preview.refetch()} />
        </CardBody>
      ) : (
        <PreviewBody
          preview={preview.data}
          isCsv={isCsv}
          running={run.isPending}
          onRun={() => run.mutate()}
          onFix={onFix}
        />
      )}
    </Card>
  );
}

function PreviewBody({
  preview,
  isCsv,
  running,
  onRun,
  onFix,
}: {
  preview: QboExportPreview;
  isCsv: boolean;
  running: boolean;
  onRun: () => void;
  onFix: (tab: string) => void;
}) {
  const [showLines, setShowLines] = useState(false);
  const c = preview.counts;
  const unmappedRows = preview.rows.filter((r) => r.status !== 'mapped');
  const reasons = [...new Set(unmappedRows.map((r) => r.status))];
  const rowsToSend = isCsv ? preview.rows.length : c.timeActivities;
  return (
    <>
      <Table className="text-xs">
        <thead>
          <tr>
            <th>Entry</th>
            <th className="r">Time activities</th>
            <th className="r">Hours</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {preview.entries.length === 0 ? (
            <tr>
              <td colSpan={4}>
                <EmptyState>Nothing approved and unexported in this scope.</EmptyState>
              </td>
            </tr>
          ) : null}
          {preview.entries.map((e) => (
            <tr key={e.entryId} data-testid="qbo-preview-row">
              <td>{e.label}</td>
              <td className="r">{e.timeActivities}</td>
              <td className="r">{hrs(e.hours)}</td>
              <td>
                <Pill tone="info">{e.status}</Pill>
                {e.unmapped ? (
                  <Pill tone="warn" className="ml-1">
                    {e.unmapped} unmapped
                  </Pill>
                ) : null}
              </td>
            </tr>
          ))}
          <tr className="tot">
            <td>{c.entries} entries</td>
            <td className="r">{c.timeActivities}</td>
            <td className="r">{hrs(c.totalHours)}</td>
            <td>{c.updates ? `${c.updates} updates` : ''}</td>
          </tr>
        </tbody>
      </Table>
      <CardBody className="flex flex-wrap items-center gap-3">
        <Button
          variant="primary"
          disabled={rowsToSend === 0 && !preview.csvOnly.included}
          loading={running}
          onClick={onRun}
          data-testid="qbo-run-export"
        >
          {isCsv
            ? `Export CSV · ${preview.rows.length} time rows`
            : `Run export · ${c.timeActivities} time activities`}
        </Button>
        <span className="text-xs text-ink3">
          Labor → {preview.laborCostCodeRule === 'single' ? 'single cost code' : 'proportional'}{' '}
          rule · {preview.billableStatus}
        </span>
        {preview.csvOnly.included ? (
          <span className="text-xs text-ink3">
            CSV only: {preview.csvOnly.quantityLines} quantity lines
            {preview.csvOnly.equipmentLines
              ? ` · ${preview.csvOnly.equipmentLines} machine lines (${hrs(preview.csvOnly.equipmentHours)} h)`
              : ''}
          </span>
        ) : null}
        {c.unmapped ? (
          <span className="text-xs font-semibold text-warn" data-testid="qbo-unmapped">
            {c.unmapped} lines skipped as UNMAPPED —{' '}
            {reasons.map((r, i) => (
              <span key={r}>
                {i ? ', ' : ''}
                <button
                  type="button"
                  className="underline"
                  onClick={() => onFix(REASON_TAB[r])}
                >
                  fix {REASON_LABEL[r] ?? r}
                </button>
              </span>
            ))}
            .{' '}
            <button type="button" className="underline" onClick={() => setShowLines((v) => !v)}>
              {showLines ? 'hide lines' : 'show lines'}
            </button>
          </span>
        ) : null}
      </CardBody>
      {showLines && unmappedRows.length ? (
        <Table className="text-xs">
          <thead>
            <tr>
              <th>Date</th>
              <th>Job</th>
              <th>Person / machine</th>
              <th className="r">Hours</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {unmappedRows.map((r) => (
              <tr key={`${r.lineId}-${r.status}`}>
                <td className="num">{fmtDateMedium(r.date)}</td>
                <td>{r.jobName}</td>
                <td>{r.employee}</td>
                <td className="r">{hrs(r.hours)}</td>
                <td>
                  <Pill tone="warn">{r.status}</Pill>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
    </>
  );
}

function SyncLog() {
  const { tz } = useCompanyBasics();
  const logs = useQuery({
    queryKey: qk.quickbooks.logs(),
    queryFn: quickbooksApi.getLogs,
    refetchInterval: (q) => (q.state.data?.some((l) => l.status === 'running') ? POLL_MS : false),
  });
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState<string | null>(null);
  const retry = useMutation({
    mutationFn: async (logId: string) => {
      const { syncLogId } = await quickbooksApi.retryFailed(logId);
      void qc.invalidateQueries({ queryKey: qk.quickbooks.logs() });
      return waitForLog(syncLogId);
    },
    onSuccess: (log) => {
      void qc.invalidateQueries({ queryKey: qk.quickbooks.all() });
      void qc.invalidateQueries({ queryKey: qk.entries.all() });
      toast.success(`Retry ${log.status} — ${log.summary ?? `${log.sent} sent`}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const tone = {
    ok: 'text-good',
    partial: 'text-warn',
    failed: 'text-bad',
    running: 'text-ink3',
  } as const;
  const glyph = {
    ok: '● ok',
    partial: '▲ partial',
    failed: '✕ failed',
    running: '… running',
  } as const;
  return (
    <Card>
      <CardHeader title="Sync log" />
      {logs.isPending ? (
        <LoadingState />
      ) : logs.isError ? (
        <CardBody>
          <ErrorState error={logs.error} />
        </CardBody>
      ) : (
        <Table className="text-xs">
          <thead>
            <tr>
              <th />
              <th>When</th>
              <th>Kind</th>
              <th>Status</th>
              <th className="r">Sent</th>
              <th className="r">Failed</th>
              <th className="r">Skipped</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {logs.data.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  <EmptyState>No runs yet.</EmptyState>
                </td>
              </tr>
            ) : null}
            {logs.data.map((l) => (
              <LogRow key={l.id} log={l} />
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );

  function LogRow({ log }: { log: QboSyncLog }) {
    const expanded = open === log.id;
    const retryable =
      log.kind === 'export' &&
      log.status !== 'running' &&
      log.failed + log.skipped > 0 &&
      !log.retriedBySyncLogId;
    return (
      <>
        <tr data-testid="qbo-log-row">
          <td className="w-6">
            {log.failures.length ? (
              <button
                type="button"
                aria-label={expanded ? 'Collapse' : 'Expand failures'}
                onClick={() => setOpen(expanded ? null : log.id)}
              >
                {expanded ? (
                  <ChevronDown className="size-4" />
                ) : (
                  <ChevronRight className="size-4" />
                )}
              </button>
            ) : null}
          </td>
          <td className="num">{fmtWhen(log.startedAt, tz)}</td>
          <td className="capitalize">
            {log.kind}
            {log.filter?.weekStart ? (
              <span className="ml-1 text-ink3">wk {fmtDateMedium(log.filter.weekStart)}</span>
            ) : null}
          </td>
          <td className={cn('font-semibold', tone[log.status])}>
            {glyph[log.status]}
            {log.summary ? <span className="ml-1 font-normal text-ink3">{log.summary}</span> : null}
          </td>
          <td className="r">{log.sent}</td>
          <td className="r">{log.failed}</td>
          <td className="r">{log.skipped}</td>
          <td className="act">
            {log.downloads.map((d) => (
              <a
                key={d.name}
                href={d.url}
                className="mr-2 inline-flex items-center gap-1 underline"
                target="_blank"
                rel="noreferrer"
              >
                <Download className="size-3" /> {d.name}
              </a>
            ))}
            {retryable ? (
              <Button
                size="sm"
                loading={retry.isPending && retry.variables === log.id}
                onClick={() => retry.mutate(log.id)}
              >
                Retry failed
              </Button>
            ) : log.retriedBySyncLogId ? (
              <span className="text-ink3">retried</span>
            ) : null}
          </td>
        </tr>
        {expanded
          ? log.failures.map((f) => (
              <tr key={f.id} className="bg-surface2">
                <td />
                <td colSpan={7} className="text-ink2">
                  <b>{f.label}</b> — <Pill tone="warn">{f.reason}</Pill> {f.message}
                </td>
              </tr>
            ))
          : null}
      </>
    );
  }
}
